import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    ExpOverdueStatus, IOverdueCase, IOverdueTransitionRequest, IOverdueWorklist, IRealisationPerformance, IUdOrderLink,
    IUdReconciliation, IMapLineItemResult } from '../interfaces/garmentsCompliance.interface';

const headers = () => AuthService.getAuthHeader();

export const UdReconciliationService = {
    get: async (institutionId: string, udId: string, tolerancePct?: number): Promise<IUdReconciliation> =>
        (await axios.get(ENDPOINTS.UD_RECONCILIATION.STATEMENT(institutionId, udId), {
            headers: headers(), params: tolerancePct !== undefined ? { tolerancePct } : {},
        })).data,
    downloadXlsx: async (institutionId: string, udId: string, tolerancePct?: number): Promise<Blob> =>
        (await axios.get(ENDPOINTS.UD_RECONCILIATION.XLSX(institutionId, udId), {
            headers: headers(), responseType: 'blob', params: tolerancePct !== undefined ? { tolerancePct } : {},
        })).data,
    downloadPdf: async (institutionId: string, udId: string, tolerancePct?: number): Promise<Blob> =>
        (await axios.get(ENDPOINTS.UD_RECONCILIATION.PDF(institutionId, udId), {
            headers: headers(), responseType: 'blob', params: tolerancePct !== undefined ? { tolerancePct } : {},
        })).data,
    linkOrder: async (institutionId: string, udId: string, buyerOrderId: string): Promise<IUdOrderLink> =>
        (await axios.post(ENDPOINTS.UD_RECONCILIATION.ORDERS(institutionId, udId), { buyerOrderId }, { headers: headers() })).data,
    unlinkOrder: async (institutionId: string, udId: string, linkId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.UD_RECONCILIATION.ORDER(institutionId, udId, linkId), { headers: headers() });
    },
    mapLineItem: async (institutionId: string, udId: string, lineId: string, itemId: string | null): Promise<IMapLineItemResult> => {
        const res = await axios.put(ENDPOINTS.UD_RECONCILIATION.LINE_ITEM(institutionId, udId, lineId), { itemId }, { headers: headers() });
        return res.data;
    },
};

export const ExpOverdueService = {
    worklist: async (institutionId: string, status?: ExpOverdueStatus | '', includeClosed = false): Promise<IOverdueWorklist> => {
        const params: Record<string, string | boolean> = { includeClosed };
        if (status) params.status = status;
        return (await axios.get(ENDPOINTS.EXP_OVERDUE.WORKLIST(institutionId), { headers: headers(), params })).data;
    },
    open: async (institutionId: string, expId: string): Promise<IOverdueCase> =>
        (await axios.post(ENDPOINTS.EXP_OVERDUE.OPEN(institutionId, expId), {}, { headers: headers() })).data,
    get: async (institutionId: string, caseId: string): Promise<IOverdueCase> =>
        (await axios.get(ENDPOINTS.EXP_OVERDUE.CASE(institutionId, caseId), { headers: headers() })).data,
    transition: async (institutionId: string, caseId: string, req: IOverdueTransitionRequest): Promise<IOverdueCase> =>
        (await axios.post(ENDPOINTS.EXP_OVERDUE.TRANSITION(institutionId, caseId), req, { headers: headers() })).data,
    saveNotes: async (institutionId: string, caseId: string, notes: string): Promise<IOverdueCase> =>
        (await axios.put(ENDPOINTS.EXP_OVERDUE.NOTES(institutionId, caseId), { notes }, { headers: headers() })).data,
};

export const RealisationPerformanceService = {
    get: async (institutionId: string, from: string, to: string): Promise<IRealisationPerformance> =>
        (await axios.get(ENDPOINTS.REALISATION_PERFORMANCE.REPORT(institutionId), { headers: headers(), params: { from, to } })).data,
    downloadXlsx: async (institutionId: string, from: string, to: string): Promise<Blob> =>
        (await axios.get(ENDPOINTS.REALISATION_PERFORMANCE.XLSX(institutionId), {
            headers: headers(), params: { from, to }, responseType: 'blob',
        })).data,
};
